import React, { useState } from 'react';
import ClaimErrorBoundary from '../ClaimErrorBoundary';
import DocumentErrorBoundary from '../documents/DocumentErrorBoundary';
import ErrorBoundary from '../ErrorBoundary';

/**
 * ErrorBoundaryDemo - Component for testing error boundaries
 * 
 * This component is used to demonstrate and test error boundary functionality.
 * It should only be used in development environments.
 */

// Component that throws different types of errors
const ErrorThrowingComponent = ({ errorType, shouldThrow }) => {
  if (!shouldThrow) {
    return <div className="p-4 bg-green-100 text-green-800 rounded">✅ Component working normally</div>;
  }

  switch (errorType) {
    case 'rendering':
      throw new Error('Rendering error: Component failed to render properly');
    case 'network':
      // This won't be caught by error boundary - demonstrating the limitation
      fetch('/non-existent-endpoint').catch(() => {
        throw new Error('Network error: This should NOT be caught by error boundary');
      });
      return <div>Network error thrown (won't be caught)</div>;
    case 'lifecycle':
      throw new Error('Lifecycle error: Error in component lifecycle');
    case 'state':
      throw new Error('State error: Invalid state update');
    case 'props':
      throw new Error('Props error: Invalid props received');
    default:
      throw new Error('Generic error: Something went wrong');
  }
};

// Test component for nested error boundaries
const NestedErrorComponent = ({ outerError, innerError }) => (
  <ErrorBoundary componentName="Outer Container">
    <div className="p-4 border-2 border-blue-300 rounded-lg">
      <h3 className="text-lg font-bold mb-2">Outer Container</h3>
      <ErrorThrowingComponent errorType="rendering" shouldThrow={outerError} />
      
      <ClaimErrorBoundary section="Inner Claims Section">
        <div className="p-4 mt-4 border-2 border-green-300 rounded-lg">
          <h4 className="font-bold mb-2">Inner Claims Section</h4>
          <ErrorThrowingComponent errorType="state" shouldThrow={innerError} />
        </div>
      </ClaimErrorBoundary>
    </div>
  </ErrorBoundary>
);

const ErrorBoundaryDemo = () => {
  const [generalError, setGeneralError] = useState(false);
  const [claimsError, setClaimsError] = useState(false);
  const [documentError, setDocumentError] = useState(false);
  const [nestedOuterError, setNestedOuterError] = useState(false);
  const [nestedInnerError, setNestedInnerError] = useState(false);

  // Only show in development
  if (import.meta.env.PROD) {
    return <div>Error Boundary Demo is only available in development mode.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Error Boundary Testing Demo</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* General Error Boundary Test */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">General Error Boundary</h2>
            <button
              onClick={() => setGeneralError(!generalError)}
              className={`mb-4 px-4 py-2 rounded ${
                generalError 
                  ? 'bg-red-600 text-white' 
                  : 'bg-blue-600 text-white'
              }`}
            >
              {generalError ? 'Reset' : 'Trigger Error'}
            </button>
            
            <ErrorBoundary 
              componentName="Test Component" 
              showDetails={true}
              fallbackMessage="This is a test error for demonstration purposes."
            >
              <ErrorThrowingComponent errorType="rendering" shouldThrow={generalError} />
            </ErrorBoundary>
          </div>

          {/* Claims Error Boundary Test */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Claims Error Boundary</h2>
            <button
              onClick={() => setClaimsError(!claimsError)}
              className={`mb-4 px-4 py-2 rounded ${
                claimsError 
                  ? 'bg-red-600 text-white' 
                  : 'bg-green-600 text-white'
              }`}
            >
              {claimsError ? 'Reset' : 'Trigger Claims Error'}
            </button>
            
            <ClaimErrorBoundary 
              section="Test Claims Form"
              customMessage="This is a test claims error for demonstration purposes."
            >
              <ErrorThrowingComponent errorType="state" shouldThrow={claimsError} />
            </ClaimErrorBoundary>
          </div>

          {/* Document Error Boundary Test */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Document Error Boundary</h2>
            <button
              onClick={() => setDocumentError(!documentError)}
              className={`mb-4 px-4 py-2 rounded ${
                documentError 
                  ? 'bg-red-600 text-white' 
                  : 'bg-orange-600 text-white'
              }`}
            >
              {documentError ? 'Reset' : 'Trigger Document Error'}
            </button>
            
            <DocumentErrorBoundary 
              operation="Document Upload Test"
              customMessage="This is a test document error for demonstration purposes."
              allowSkip={true}
            >
              <ErrorThrowingComponent errorType="props" shouldThrow={documentError} />
            </DocumentErrorBoundary>
          </div>

          {/* Nested Error Boundaries Test */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Nested Error Boundaries</h2>
            <div className="space-y-2 mb-4">
              <button
                onClick={() => setNestedOuterError(!nestedOuterError)}
                className={`px-3 py-1 rounded text-sm ${
                  nestedOuterError 
                    ? 'bg-red-500 text-white' 
                    : 'bg-purple-600 text-white'
                }`}
              >
                {nestedOuterError ? 'Reset Outer' : 'Outer Error'}
              </button>
              <button
                onClick={() => setNestedInnerError(!nestedInnerError)}
                className={`ml-2 px-3 py-1 rounded text-sm ${
                  nestedInnerError 
                    ? 'bg-red-500 text-white' 
                    : 'bg-indigo-600 text-white'
                }`}
              >
                {nestedInnerError ? 'Reset Inner' : 'Inner Error'}
              </button>
            </div>
            
            <NestedErrorComponent 
              outerError={nestedOuterError}
              innerError={nestedInnerError}
            />
          </div>
        </div>

        {/* Information Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-4">Error Boundary Testing Guide</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p><strong>General Error Boundary:</strong> Catches all types of rendering and lifecycle errors with generic fallback UI.</p>
            <p><strong>Claims Error Boundary:</strong> Specialized for claims-related components with claims-specific messaging and recovery options.</p>
            <p><strong>Document Error Boundary:</strong> Designed for document operations with file-specific troubleshooting tips.</p>
            <p><strong>Nested Boundaries:</strong> Demonstrates how inner error boundaries can catch errors while outer boundaries remain functional.</p>
            
            <div className="mt-4 p-3 bg-blue-100 rounded">
              <p className="font-bold">Important Notes:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>Error boundaries only catch errors in rendering, lifecycle, and constructors</li>
                <li>They do NOT catch errors in event handlers, async code, or during SSR</li>
                <li>Network errors must be handled separately in async functions</li>
                <li>Error boundaries should be placed strategically throughout the component tree</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundaryDemo;
